import {useForm} from '@tanstack/react-form';
import React from 'react';
import {Button} from '~/components/Button';
import {Input, InputType} from '~/components/Controls/Input';
import {FormColumn, FormControls, FormRow, FormTitle, IntentText} from '~/components/Form';
import formStyle from '~/components/Form/index.module.scss';
import {IModalComponent, IModalComponentClose} from '~/components/Modal';
import {Intent} from '~/lib/intent';

export interface IScaleSpendLimitsFormActions {
    setStatus: (status: string | undefined) => void;
}

export interface IScaleSpendLimitsValues {
    factor: number;
}

export interface IScaleSpendLimitsModalProps {
    onSubmit: (values: IScaleSpendLimitsValues, formActions: IScaleSpendLimitsFormActions, close: IModalComponentClose) => void | Promise<void>;
}

export const ScaleSpendLimitsModal: IModalComponent<IScaleSpendLimitsModalProps> = props => {
    const closeModal = props.close ?? (() => undefined);
    const [status, setStatus] = React.useState<string>();

    const form = useForm({
        defaultValues: {factor: 1} as IScaleSpendLimitsValues,
        onSubmit: async ({value}) => {
            await Promise.resolve(props.onSubmit(value, {setStatus}, closeModal));
        }
    });

    return <form.Subscribe selector={state => state}>
        {state => (
            <form className={formStyle.form} onSubmit={event => {
                event.preventDefault();
                void form.handleSubmit();
            }}>
                <FormTitle>Scale spend limits</FormTitle>
                {status && <IntentText intent={Intent.DANGER}>{status}</IntentText>}
                <div className={formStyle.formBody}>
                    <FormRow>
                        <FormColumn>
                            <form.Field
                                name='factor'
                                validators={{
                                    onChange: ({value}) => Number.isFinite(value) && value >= 0.1 && value <= 5 ? undefined : 'Factor must be between 0.1 and 5'
                                }}
                            >
                                {fieldApi => (
                                    <>
                                        <Input id='scale-factor'
                                            type={InputType.FLOAT}
                                            min={0.1}
                                            max={5}
                                            step={0.1}
                                            value={Number.isFinite(fieldApi.state.value) ? String(fieldApi.state.value) : ''}
                                            intent={fieldApi.state.meta.errors.length > 0 ? Intent.DANGER : undefined}
                                            disabled={state.isSubmitting}
                                            onChange={text => fieldApi.handleChange(text.trim() === '' ? NaN : Number(text))}
                                        />
                                        {fieldApi.state.meta.errors.length > 0 && <IntentText intent={Intent.DANGER}>{fieldApi.state.meta.errors.join(', ')}</IntentText>}
                                    </>
                                )}
                            </form.Field>
                            <ul>
                                <li>Scaling factor holds for 7 days.</li>
                                <li>Every hour is rounded up to the next $100, so no hour drops below $100.</li>
                                <li>Scaling again multiplies the current factor by the new one and starts a new 7 days.</li>
                                <li>A day without limits starts from the fallback cap.</li>
                            </ul>
                        </FormColumn>
                    </FormRow>
                </div>
                <FormControls>
                    <Button disabled={state.isSubmitting}
                        onClick={closeModal}
                    >Cancel</Button>
                    <Button type='submit'
                        disabled={state.isSubmitting || !state.canSubmit}
                        intent={Intent.PRIMARY}
                        rightIcon={state.isSubmitting && <i className='fa fa-spinner fa-spin'/>}
                    >Scale</Button>
                </FormControls>
            </form>
        )}
    </form.Subscribe>;
};
